import React from 'react';
import { Shield, Mail, Lock, Eye, FileText } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-emerald-900 to-emerald-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-4">
            <Shield className="w-12 h-12 mr-4" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold">Privacy Policy</h1>
              <p className="text-emerald-100 mt-2">Last Updated: November 2025</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-md p-8">
          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Raven Environmental Consulting, LLC ("we," "our," or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you 
              visit our website and use our services.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By using our website and services, you agree to the collection and use of information in accordance 
              with this policy. If you do not agree with our policies and practices, please do not use our services.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-8">
            <div className="flex items-center mb-4">
              <FileText className="w-6 h-6 text-emerald-700 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Information We Collect</h2>
            </div>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Personal Information</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
              <li>Fill out contact forms or inquiry forms</li>
              <li>Request information about our services</li>
              <li>Submit project inquiries</li>
              <li>Subscribe to newsletters or communications</li>
              <li>Contact us via email or phone</li>
            </ul>
            
            <p className="text-gray-700 leading-relaxed mb-4">
              The personal information we collect may include:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2 ml-4">
              <li>Name (first and last)</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Company/Organization name</li>
              <li>Project location and details</li>
              <li>Facility information</li>
              <li>Technical specifications related to environmental projects</li>
              <li>Any other information you choose to provide</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Automatically Collected Information</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you visit our website, we may automatically collect certain information about your device, including:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring website addresses</li>
              <li>Date and time of visits</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-8">
            <div className="flex items-center mb-4">
              <Eye className="w-6 h-6 text-emerald-700 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">How We Use Your Information</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
              <li>To respond to your inquiries and provide requested information</li>
              <li>To provide environmental consulting services</li>
              <li>To prepare proposals and quotes for your projects</li>
              <li>To communicate about your projects and services</li>
              <li>To send administrative information and updates</li>
              <li>To improve our website and services</li>
              <li>To analyze usage patterns and trends</li>
              <li>To comply with legal obligations</li>
              <li>To protect our rights and prevent fraud</li>
            </ul>
          </section>

          {/* Information Sharing */}
          <section className="mb-8">
            <div className="flex items-center mb-4">
              <Lock className="w-6 h-6 text-emerald-700 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Information Sharing and Disclosure</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share your 
              information only in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
              <li><strong>Service Providers:</strong> We may share information with trusted service providers 
                  who assist us in operating our website and providing services, under strict confidentiality agreements</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law, regulation, 
                  legal process, or governmental request</li>
              <li><strong>Business Transfers:</strong> In connection with any merger, sale of company assets, 
                  or acquisition, your information may be transferred</li>
              <li><strong>Protection of Rights:</strong> To protect and defend our rights, property, or safety, 
                  or that of our clients or others</li>
              <li><strong>With Your Consent:</strong> We may share information with third parties when you 
                  give us explicit permission to do so</li>
            </ul>
          </section>

          {/* Data Security */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Security</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We implement appropriate technical and organizational security measures to protect your personal 
              information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
              <li>Secure server infrastructure</li>
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments</li>
              <li>Limited access to personal information</li>
              <li>Employee training on data protection</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              However, no method of transmission over the Internet or electronic storage is 100% secure. 
              While we strive to protect your personal information, we cannot guarantee absolute security.
            </p>
          </section>

          {/* Data Retention */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Retention</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We retain your personal information for as long as necessary to fulfill the purposes outlined 
              in this Privacy Policy, unless a longer retention period is required or permitted by law. 
              The criteria used to determine our retention periods include:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
              <li>The duration of our relationship with you</li>
              <li>Legal obligations to retain data</li>
              <li>Applicable statutes of limitations</li>
              <li>Business and operational needs</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights and Choices</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You have certain rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
              <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information, subject to legal obligations</li>
              <li><strong>Objection:</strong> Object to processing of your personal information</li>
              <li><strong>Portability:</strong> Request transfer of your information to another service provider</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              To exercise these rights, please contact us using the information provided below.
            </p>
          </section>

          {/* Cookies */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our website may use cookies and similar tracking technologies to enhance your experience. 
              Cookies are small text files stored on your device that help us:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
              <li>Remember your preferences</li>
              <li>Understand how you use our website</li>
              <li>Improve website functionality</li>
              <li>Analyze website traffic and usage patterns</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              You can control cookie settings through your browser. However, disabling cookies may affect 
              the functionality of our website.
            </p>
          </section>

          {/* Third-Party Links */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Links</h2>
            <p className="text-gray-700 leading-relaxed">
              Our website may contain links to third-party websites. We are not responsible for the privacy 
              practices or content of these external sites. We encourage you to review the privacy policies 
              of any third-party sites you visit.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Our services are not directed to individuals under the age of 18. We do not knowingly collect 
              personal information from children. If we learn that we have collected information from a child 
              under 18, we will delete that information promptly.
            </p>
          </section>

          {/* Changes to Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices or for 
              legal, operational, or regulatory reasons. We will post the updated policy on this page with 
              a new "Last Updated" date. We encourage you to review this Privacy Policy periodically.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-8">
            <div className="flex items-center mb-4">
              <Mail className="w-6 h-6 text-emerald-700 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Contact Us</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, 
              please contact us:
            </p>
            <div className="bg-emerald-50 p-6 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">Raven Environmental Consulting, LLC</p>
              <p className="text-gray-700 mb-1">Email: info@raven-environmental.com</p>
              <p className="text-gray-700 mb-1">Phone: (907) 687-8263</p>
              <p className="text-gray-700">Located in Fairbanks, Alaska</p>
            </div>
          </section>

          {/* Alaska-Specific Notice */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Alaska Residents</h2>
            <p className="text-gray-700 leading-relaxed">
              As an Alaska-based company, we comply with all applicable Alaska state laws regarding data 
              privacy and protection. If you are an Alaska resident and have specific questions about your 
              privacy rights, please contact us.
            </p>
          </section>

          {/* Consent */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Consent</h2>
            <p className="text-gray-700 leading-relaxed">
              By using our website and services, you acknowledge that you have read and understood this 
              Privacy Policy and agree to its terms. If you do not agree with this policy, please do not 
              use our website or services.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
